import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { ParkingFormData, LoungeFormData } from '@white-label-types/search-box';
import { getSearchBoxFields, createSearchBoxConfig } from './helpers/search-box-config';
import { ROUTE_NAMES } from '@white-label-configuration/constants';

export type SearchBoxFormData = ParkingFormData | LoungeFormData;

type Timestamp = Date | string | null;
type SearchBoxParams = { [key: string]: string };

export const useSearchBoxStore = defineStore('searchBox', () => {
  const searchBoxFormData = ref<SearchBoxFormData>({
    entryDate: '',
    entryTime: null,
    exitDate: '',
    exitTime: null,
    terminal: undefined,
    date: '',
    discount: null,
    outboundFlight: null,
    customers: {
      adult: 0,
      child: 0,
      senior: 0,
      infant: 0,
    },
  });
  const params = ref<SearchBoxParams>({});
  const formType = ref<string>('');
  const searchBoxTimestamp = ref<Timestamp>(null);

  const fieldConfigByStep = computed(() => {
    return {
      [ROUTE_NAMES.home]: {
        config: createSearchBoxConfig(searchBoxFormData.value, 'step1'),
        fields: getSearchBoxFields(searchBoxFormData.value, 'step1'),
      },
      [ROUTE_NAMES.search]: {
        config: createSearchBoxConfig(searchBoxFormData.value, 'step2'),
        fields: getSearchBoxFields(searchBoxFormData.value, 'step2'),
      },
      [ROUTE_NAMES.travel_extras]: {
        config: createSearchBoxConfig(searchBoxFormData.value, 'step3'),
        fields: getSearchBoxFields(searchBoxFormData.value, 'step3'),
      },
    }
  });

  function updateStoreData(payload: { formData: SearchBoxFormData, timestamp: Timestamp }) {
    searchBoxFormData.value = { ...payload.formData };
    searchBoxTimestamp.value = payload.timestamp;
  }

  function clearStoreData() {
    searchBoxFormData.value = {
      entryDate: '',
      entryTime: null,
      exitDate: '',
      exitTime: null,
      terminal: undefined,
      date: '',
      discount: null,
      outboundFlight: null,
    };
    params.value = {};
    formType.value = '';
    searchBoxTimestamp.value = null;
  }

  function clearDiscount() {
    searchBoxFormData.value.discount = null;
  }

  function saveSearchBoxParams(payload: { id: string; data: object }) {
    if (payload.id && payload.data && params.value)
      params.value[payload.id] = JSON.stringify(payload.data);
  }

  function getSearchBoxParams(id: string) {
    return params.value && params.value[id] ? JSON.parse(params.value[id]) : {};
  }

  return {
    searchBoxFormData,
    formType,
    searchBoxTimestamp,
    fieldConfigByStep,
    updateStoreData,
    clearStoreData,
    clearDiscount,
    saveSearchBoxParams,
    getSearchBoxParams,
  };
},
  {
    persist: true,
  },
);
